import { Box, useColorModeValue } from "@chakra-ui/react";
import React, { useCallback, useMemo, useRef } from "react";

import {
  FieldFilterSelect,
  FieldMultiFilterSelect,
} from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import { PlusIcon, type PlusIconHandle } from "@/components/ui/plus-icon";
import {
  ProfitLossFilter,
  TradeFilters,
  TradeStatus,
  TradeType,
} from "../../../../types/trade";

interface TradeLogActionBarProps {
  filters: TradeFilters;
  onFiltersChange: (filters: TradeFilters) => void;
  onLogNewTrade: () => void;
  onExport?: () => void;
  onImport?: () => void;
  onManageTemplates?: () => void;
  availableSymbols?: string[];
}

function TradeLogFilterFields({
  filters,
  onFiltersChange,
  availableSymbols,
}: Pick<
  TradeLogActionBarProps,
  "filters" | "onFiltersChange" | "availableSymbols"
>) {
  const handleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      status: value === "all" ? undefined : (value as TradeStatus),
    });
  };

  const handleTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      type: value === "all" ? undefined : (value as TradeType),
    });
  };

  const handleProfitLossChange = (value: string) => {
    onFiltersChange({
      ...filters,
      profitLoss:
        value === "all" ? undefined : (value as ProfitLossFilter),
    });
  };

  const handleSymbolToggle = (symbol: string) => {
    const currentSymbols = filters.symbols || [];
    const newSymbols = currentSymbols.includes(symbol)
      ? currentSymbols.filter((s) => s !== symbol)
      : [...currentSymbols, symbol];
    onFiltersChange({
      ...filters,
      symbols: newSymbols.length > 0 ? newSymbols : undefined,
    });
  };

  return (
    <>
      <FieldFilterSelect
        value={filters.status ?? "all"}
        onValueChange={handleStatusChange}
        options={[
          { value: "all", label: "All Status" },
          { value: "open", label: "Open" },
          { value: "closed", label: "Closed" },
        ]}
      />
      <FieldFilterSelect
        value={filters.type ?? "all"}
        onValueChange={handleTypeChange}
        options={[
          { value: "all", label: "All Types" },
          { value: "buy", label: "Buy" },
          { value: "sell", label: "Sell" },
        ]}
      />
      <FieldFilterSelect
        value={filters.profitLoss ?? "all"}
        onValueChange={handleProfitLossChange}
        options={[
          { value: "all", label: "All Trades" },
          { value: "winning", label: "Winning" },
          { value: "losing", label: "Losing" },
        ]}
      />
      <FieldMultiFilterSelect
        options={availableSymbols ?? []}
        selected={filters.symbols}
        onToggle={handleSymbolToggle}
        emptyLabel="All Symbols"
        getTriggerLabel={(selected) => {
          if (selected.length === 0) return "All Symbols";
          if (selected.length === 1) return selected[0];
          return `${selected.length} Symbols`;
        }}
      />
    </>
  );
}

export function TradeLogLogNewTradeButton({ onClick }: { onClick: () => void }) {
  const iconRef = useRef<PlusIconHandle | null>(null);

  const startIconAnimation = useCallback(() => {
    void iconRef.current?.startAnimation();
  }, []);

  const stopIconAnimation = useCallback(() => {
    void iconRef.current?.stopAnimation();
  }, []);

  const iconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  );

  return (
    <Button
      type="button"
      variant="gradient"
      size="sm"
      data-font="primary"
      className="shrink-0 normal-case tracking-normal"
      onClick={onClick}
      {...iconHoverHandlers}
    >
      <PlusIcon
        ref={iconRef}
        data-icon="inline-start"
        size={16}
        className="shrink-0 text-current"
        aria-hidden
      />
      <span>Log New Trade</span>
    </Button>
  );
}

/** Filter selects — left side of the shadcn data-table toolbar row. */
export function TradeLogTableToolbar({
  filters,
  onFiltersChange,
  availableSymbols = ["EURUSD", "GBPUSD", "USDJPY", "AUDUSD", "USDCAD"],
  variant = "row",
  trailing,
}: Omit<TradeLogActionBarProps, "onLogNewTrade" | "onExport" | "onImport"> & {
  variant?: "row" | "grid";
  trailing?: React.ReactNode;
}) {
  const isGrid = variant === "grid";

  if (isGrid) {
    return (
      <div
        data-trade-log-action-bar=""
        data-trade-log-filter-bar=""
        data-trade-log-filter-bar-grid=""
        className="grid w-full min-w-0 grid-cols-2 gap-2 md:grid-cols-3 lg:flex lg:flex-1 lg:items-center lg:gap-3"
      >
        <TradeLogFilterFields
          filters={filters}
          onFiltersChange={onFiltersChange}
          availableSymbols={availableSymbols}
        />
        {trailing ? <div className="min-w-0 lg:shrink-0">{trailing}</div> : null}
      </div>
    );
  }

  return (
    <div
      data-trade-log-action-bar=""
      data-trade-log-filter-bar=""
      className="flex min-w-0 flex-1 items-center gap-3"
    >
      <div
        data-trade-log-action-bar-filters=""
        data-trade-log-filter-bar-filters=""
        className="flex min-w-0 shrink-0 items-center gap-3 overflow-x-auto"
      >
        <TradeLogFilterFields
          filters={filters}
          onFiltersChange={onFiltersChange}
          availableSymbols={availableSymbols}
        />
      </div>
      {trailing}
    </div>
  );
}

/** @deprecated Use TradeLogTableToolbar in the data-table toolbar row. */
const TradeLogActionBar: React.FC<TradeLogActionBarProps> = ({
  onLogNewTrade,
  onExport: _onExport,
  onImport: _onImport,
  ...toolbarProps
}) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      p={{ base: 3, md: 4 }}
      w="100%"
    >
      <div className="flex w-full items-center gap-3">
        <TradeLogTableToolbar {...toolbarProps} />
        <TradeLogLogNewTradeButton onClick={onLogNewTrade} />
      </div>
    </Box>
  );
};

export default TradeLogActionBar;
